import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, CheckCheck, CheckCircle2, Info } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { NotificationsApi } from "../lib/api";
import { formatMoney, relativeTime } from "../lib/format";
import { cn } from "../lib/utils";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";

const POLL_MS = 30000;

const ICONS = {
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Info,
};

const TONES = {
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  success: "bg-primary/10 text-primary",
  info: "bg-muted text-muted-foreground",
};

/** Bell + popover feed of in-app notifications for the signed-in user. */
export const NotificationBell = ({ className }) => {
  const { t } = useI18n();
  const { currency, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadCount = useCallback(() => {
    if (!user) return;
    NotificationsApi.unreadCount()
      .then((d) => setUnread(d.unread || 0))
      .catch(() => {});
  }, [user]);

  const loadFeed = useCallback(() => {
    setLoading(true);
    NotificationsApi.feed({ limit: 30 })
      .then((d) => {
        setItems(d.notifications || []);
        setUnread(d.unread || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCount();
    const timer = setInterval(loadCount, POLL_MS);
    return () => clearInterval(timer);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadFeed();
  }, [open, loadFeed]);

  const describe = useCallback(
    (n) => {
      const p = n.params || {};
      const cur = p.currency || currency || "EUR";
      const vars = {
        ...p,
        amount: p.amount != null ? formatMoney(p.amount, cur) : "—",
        refund: p.refund != null ? formatMoney(p.refund, cur) : "—",
        reason: p.reason || "—",
      };
      const title = t(`alerts.kinds.${n.kind}`);
      const body = t(`alerts.bodies.${n.kind}`, vars);
      return {
        title: title.startsWith("alerts.") ? n.kind.replace(/_/g, " ") : title,
        body: body.startsWith("alerts.") ? "" : body,
      };
    },
    [currency, t]
  );

  const markRead = useCallback((id) => {
    setItems((list) => list.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    setUnread((c) => Math.max(0, c - 1));
    NotificationsApi.markRead(id).catch(() => {});
  }, []);

  const markAll = useCallback(() => {
    setItems((list) => list.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    setUnread(0);
    NotificationsApi.markAllRead().catch(() => {});
  }, []);

  const openItem = useCallback(
    (n) => {
      if (!n.read_at) markRead(n.id);
      if (n.link) {
        setOpen(false);
        navigate(n.link);
      }
    },
    [markRead, navigate]
  );

  const badgeLabel = useMemo(() => (unread > 9 ? "9+" : String(unread)), [unread]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn("relative", className)}
          aria-label={t("alerts.bell")}
          data-testid="notification-bell-button"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 ? (
            <span
              className="ll-num absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
              data-testid="notification-unread-badge"
            >
              {badgeLabel}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(24rem,calc(100vw-2rem))] p-0"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between gap-2 px-3 py-2.5">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-sm font-semibold">{t("alerts.title")}</h2>
            {unread > 0 ? (
              <Badge variant="secondary" className="ll-num" data-testid="notification-unread-count">
                {t("alerts.unread", { n: unread })}
              </Badge>
            ) : null}
          </div>
          {unread > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={markAll}
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="h-3.5 w-3.5" /> {t("alerts.markAll")}
            </Button>
          ) : null}
        </div>
        <Separator />

        {loading && !items.length ? (
          <div className="space-y-2 p-3">
            <div className="h-12 animate-pulse rounded-lg bg-muted" />
            <div className="h-12 animate-pulse rounded-lg bg-muted" />
          </div>
        ) : items.length ? (
          <ScrollArea className="max-h-[22rem]">
            <ul className="divide-y" data-testid="notification-list">
              {items.map((n) => {
                const { title, body } = describe(n);
                const Icon = ICONS[n.severity] || Info;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openItem(n)}
                      className={cn(
                        "flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors duration-150",
                        "hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
                        !n.read_at && "bg-primary/[0.04]"
                      )}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
                          TONES[n.severity] || TONES.info
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{title}</span>
                          {!n.read_at ? (
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                          ) : null}
                        </span>
                        {body ? (
                          <span className="mt-0.5 block text-xs text-muted-foreground">{body}</span>
                        ) : null}
                        <span className="mt-0.5 block text-[11px] text-muted-foreground/80">
                          {relativeTime(n.created_at) || t("alerts.justNow")}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        ) : (
          <div className="px-4 py-8 text-center" data-testid="notification-empty">
            <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              <Bell className="h-4 w-4 text-muted-foreground" />
            </span>
            <p className="mt-2 text-sm font-medium">{t("alerts.emptyTitle")}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("alerts.emptyBody")}</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
